import { type MouseEvent } from 'react';
import { Box, Group, Table } from '@mantine/core';
import { FeatureFlagResponse, FeatureFlagStatus } from './useAdminFeatureFlags';
import { CellState, STATE_META } from './flagMatrixTypes';

export type TenantColumn = {
  tenantName: string;
  label: string;
  internal: boolean;
  error: string | null;
};

type Props = {
  flags: FeatureFlagResponse[];
  tenants: TenantColumn[];
  stateFor: (flagKey: string, tenantName: string) => CellState;
  onSelectCell: (flagKey: string, tenantName: string) => void;
};

const GLOBAL_DOT: Record<FeatureFlagStatus, string> = {
  fully_released: 'green.6',
  partially_released: 'yellow.5',
  disabled: 'gray.5',
};

const bodyBg = 'var(--mantine-color-body)';
const cornerStyle = {
  position: 'sticky',
  left: 0,
  top: 0,
  zIndex: 6,
  background: bodyBg,
  minWidth: 260,
  width: 260,
  padding: '8px 12px',
  verticalAlign: 'bottom',
} as const;
const tenantHeadStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 4,
  background: bodyBg,
  height: 150,
  width: 24,
  minWidth: 24,
  maxWidth: 24,
  padding: '0 0 8px',
  verticalAlign: 'bottom',
} as const;
const rotatedLabel = {
  writingMode: 'vertical-rl',
  transform: 'rotate(180deg)',
  whiteSpace: 'nowrap',
  fontFamily: 'var(--mantine-font-family-monospace)',
  fontSize: 10.5,
  lineHeight: '24px',
  margin: '0 auto',
} as const;
const flagHeadStyle = {
  position: 'sticky',
  left: 0,
  zIndex: 3,
  background: bodyBg,
  minWidth: 260,
  width: 260,
  padding: '4px 12px',
} as const;
const flagNameStyle = {
  fontFamily: 'var(--mantine-font-family-monospace)',
  fontSize: 11.5,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 234,
} as const;
const cellStyle = { width: 24, minWidth: 24, padding: 0, cursor: 'pointer' } as const;

function tenantLabelColour(tenant: TenantColumn): string {
  if (tenant.error) return STATE_META.failed.colour;
  return tenant.internal ? 'blue.6' : 'dimmed';
}

export default function FlagMatrixGrid({ flags, tenants, stateFor, onSelectCell }: Props) {
  const handleClick = (event: MouseEvent<HTMLTableSectionElement>) => {
    const cell = (event.target as HTMLElement).closest('td[data-flag]');
    if (!(cell instanceof HTMLElement)) return;
    const { flag, tenant } = cell.dataset;
    if (flag && tenant) onSelectCell(flag, tenant);
  };

  return (
    <Box style={{ overflow: 'auto', maxHeight: 640 }}>
      <Table
        withColumnBorders
        withRowBorders
        withTableBorder={false}
        horizontalSpacing={0}
        verticalSpacing={0}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={cornerStyle}>flag \ tenant</Table.Th>
            {tenants.map(tenant => (
              <Table.Th
                key={tenant.tenantName}
                style={tenantHeadStyle}
                title={tenant.error ?? undefined}
              >
                <Box
                  style={rotatedLabel}
                  c={tenantLabelColour(tenant)}
                  fw={tenant.internal || tenant.error ? 600 : undefined}
                >
                  {tenant.label}
                </Box>
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody onClick={handleClick}>
          {flags.map(flag => (
            <Table.Tr key={flag.key}>
              <Table.Th style={flagHeadStyle}>
                <Group gap={6} wrap="nowrap">
                  <Box
                    w={6}
                    h={6}
                    bg={GLOBAL_DOT[flag.status]}
                    style={{ borderRadius: 999, flex: 'none' }}
                  />
                  <Box style={flagNameStyle}>{flag.key}</Box>
                </Group>
              </Table.Th>
              {tenants.map(tenant => {
                const meta = STATE_META[stateFor(flag.key, tenant.tenantName)];
                return (
                  <Table.Td
                    key={tenant.tenantName}
                    data-flag={flag.key}
                    data-tenant={tenant.tenantName}
                    bg={meta.colour}
                    style={cellStyle}
                    title={`${flag.key} · ${tenant.tenantName} — ${tenant.error ?? meta.label}`}
                  />
                );
              })}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
}
